import React, { useState } from "react";
import {
	Brain,
	ChevronDown,
	ChevronRight,
	FileSearch,
	FileText,
	Search,
	CheckCircle2,
	ExternalLink,
} from "lucide-react";
import {
	STEP_LABEL,
	SOURCE_TYPE_LABEL,
	countSourcesByType,
	getSortedQueries,
	getSourcesForQuery,
	groupSourcesByType,
	splitSearchPhrases,
	sourceSubtitle,
	sourceUrlLabel,
} from "./fanoutUtils";

const SOURCE_GROUP_ORDER = ["mentioned", "cited", "fetched"];

const STEP_ICON = {
	reasoning: Brain,
	search: Search,
	open_page: FileText,
	find_in_page: FileSearch,
	answer: CheckCircle2,
};

function SourceGroupList({ type, sources }) {
	if (!sources.length) return null;
	return (
		<div className="monitoring__fanout-source-group">
			<span
				className={`monitoring__fanout-source-label monitoring__fanout-source-label--${type}`}
			>
				{SOURCE_TYPE_LABEL[type]} ({sources.length})
			</span>
			<ol className="monitoring__fanout-timeline-sources">
				{sources.map((source) => (
					<li key={source.id}>
						<a
							href={source.url}
							target="_blank"
							rel="noopener noreferrer"
							className="monitoring__fanout-timeline-source-link"
						>
							<span className="monitoring__fanout-timeline-source-primary">
								{sourceUrlLabel(source)}
							</span>
							{sourceSubtitle(source) ? (
								<span className="monitoring__fanout-timeline-source-secondary">
									{sourceSubtitle(source)}
								</span>
							) : null}
							<ExternalLink size={12} aria-hidden className="ms-1" />
						</a>
					</li>
				))}
			</ol>
		</div>
	);
}

function TimelineStep({ query, analysis }) {
	const [expanded, setExpanded] = useState(false);
	const Icon = STEP_ICON[query.action_type] || Search;
	const sourcesForStep = getSourcesForQuery(analysis, query.id);
	const counts = countSourcesByType(sourcesForStep);
	const hasSources = sourcesForStep.length > 0;
	const phrases =
		query.action_type === "search" ? splitSearchPhrases(query.query_text) : [];

	return (
		<li className="monitoring__analysis-timeline-step">
			<div className="monitoring__analysis-timeline-step-head">
				<div className="monitoring__analysis-timeline-step-meta">
					<span className="monitoring__analysis-step-num">
						Step {query.seq_order + 1}
					</span>
					<span className="monitoring__analysis-step-type monitoring__analysis-step-type--with-icon">
						<Icon size={12} aria-hidden />
						{STEP_LABEL[query.action_type] || query.action_type}
					</span>
				</div>
				<div className="monitoring__fanout-timeline-step-actions">
					{(counts.cited > 0 || counts.mentioned > 0 || counts.fetched > 0) && (
						<div className="monitoring__analysis-step-badges">
							{counts.cited > 0 && (
								<span className="monitoring__badge monitoring__badge--cited">
									cited {counts.cited}
								</span>
							)}
							{counts.mentioned > 0 && (
								<span className="monitoring__badge monitoring__badge--mentioned">
									mentioned {counts.mentioned}
								</span>
							)}
							{counts.fetched > 0 && (
								<span className="monitoring__badge monitoring__badge--fetched">
									researched {counts.fetched}
								</span>
							)}
						</div>
					)}
					{hasSources ? (
						<button
							type="button"
							className="monitoring__fanout-timeline-toggle btn btn-link btn-sm p-0"
							onClick={() => setExpanded((value) => !value)}
							aria-expanded={expanded}
						>
							{expanded ? (
								<ChevronDown size={14} aria-hidden />
							) : (
								<ChevronRight size={14} aria-hidden />
							)}
							{expanded ? "Hide sources" : "Show sources"}
						</button>
					) : null}
				</div>
			</div>
			{phrases.length > 0 ? (
				<ul className="monitoring__fanout-timeline-phrases mb-0">
					{phrases.map((phrase, index) => (
						<li key={index}>{phrase}</li>
					))}
				</ul>
			) : query.query_text ? (
				<p className="monitoring__analysis-timeline-step-text mb-0">{query.query_text}</p>
			) : null}
			{expanded && hasSources ? (
				<div className="monitoring__fanout-timeline-sources-wrap">
					{SOURCE_GROUP_ORDER.map((type) => (
						<SourceGroupList
							key={type}
							type={type}
							sources={groupSourcesByType(sourcesForStep)[type]}
						/>
					))}
				</div>
			) : null}
		</li>
	);
}

export default function FanoutTimelineView({ analysis }) {
	const queries = getSortedQueries(analysis);

	return (
		<div className="monitoring__analysis-panel monitoring__analysis-panel--timeline monitoring__fanout-view-panel">
			<h4 className="monitoring__analysis-panel-title">Generation timeline</h4>
			{queries.length === 0 ? (
				<p className="text-muted small mb-0">No steps captured for this provider.</p>
			) : (
				<ul className="monitoring__analysis-timeline monitoring__analysis-timeline--fanout">
					{queries.map((query) => (
						<TimelineStep key={query.id} query={query} analysis={analysis} />
					))}
				</ul>
			)}
		</div>
	);
}
